import { useState, useEffect } from 'preact/hooks';
import { 
  Terminal, 
  User, 
  GitBranch, 
  Clock, 
  DollarSign, 
  Activity,
  Wifi,
  WifiOff,
  FolderOpen,
  Bot,
  Settings
} from 'lucide-preact';

interface StatusLineProps {
  currentProject?: {
    id: string;
    name: string;
    path: string;
    type: string;
  } | null;
  isTerminalConnected: boolean;
  isAuthenticated: boolean;
  user?: {
    email: string;
    organization?: string;
  };
  terminalOutput?: string;
  todos?: any[];
  subagents?: any[];
  currentView?: string;
}

interface GitInfo {
  branch?: string;
  status?: string;
  hasChanges?: boolean;
}

export function StatusLine({
  currentProject,
  isTerminalConnected,
  isAuthenticated,
  user,
  terminalOutput,
  todos,
  subagents,
  currentView
}: StatusLineProps) {
  // Early return if no project (shouldn't happen since we only render when project exists)
  if (!currentProject) {
    return null;
  }

  const [gitInfo, setGitInfo] = useState<GitInfo>({});
  const [cost, setCost] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [linesAdded, setLinesAdded] = useState<number>(0);
  const [linesRemoved, setLinesRemoved] = useState<number>(0);

  // Fetch git information
  useEffect(() => {
    if (currentProject) {
      fetchGitInfo(currentProject.id);
    }
  }, [currentProject]);

  // Parse metrics from terminal output
  useEffect(() => {
    if (terminalOutput) {
      parseMetrics(terminalOutput);
    }
  }, [terminalOutput]);

  const fetchGitInfo = async (projectId: string) => {
    try {
      const response = await fetch(`/api/projects/${projectId}/git`);
      if (response.ok) {
        const gitData = await response.json();
        setGitInfo({
          branch: gitData.branch,
          status: gitData.status,
          hasChanges: gitData.hasChanges
        });
      }
    } catch (error) {
      console.error('Failed to fetch git info:', error);
    }
  };

  const parseMetrics = (output: string) => {
    // Parse cost and performance metrics from terminal output
    const costMatch = output.match(/cost[:\s]+(\d+\.?\d*)/i);
    const durationMatch = output.match(/duration[:\s]+(\d+)/i);
    const addedMatch = output.match(/added[:\s]+(\d+)/i);
    const removedMatch = output.match(/removed[:\s]+(\d+)/i);

    if (costMatch) setCost(parseFloat(costMatch[1]));
    if (durationMatch) setDuration(parseInt(durationMatch[1]));
    if (addedMatch) setLinesAdded(parseInt(addedMatch[1]));
    if (removedMatch) setLinesRemoved(parseInt(removedMatch[1]));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'clean': return 'text-green-500';
      case 'dirty': return 'text-yellow-500';
      case 'error': return 'text-red-500';
      default: return 'text-muted-foreground';
    }
  };

  const formatCost = (cost: number) => {
    return cost > 0 ? `$${cost.toFixed(4)}` : '$0.0000';
  };

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  return (
    <div className="status-line px-4 py-2 text-xs font-mono text-foreground">
      <div className="flex items-center justify-between">
        {/* Left side - Project and Git info */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1">
            <FolderOpen className="h-3 w-3 text-blue-500" />
            <span className="text-blue-500">{currentProject.name}</span>
            <span className="text-muted-foreground">({currentProject.type})</span>
          </div>
          
          {gitInfo.branch && (
            <div className="flex items-center gap-1">
              <GitBranch className="h-3 w-3 text-green-500" />
              <span className="text-green-500">{gitInfo.branch}</span>
              {gitInfo.hasChanges && (
                <span className="text-yellow-500">*</span>
              )}
            </div>
          )}
          
          <div className="flex items-center gap-1">
            <span className="text-muted-foreground">View:</span>
            <span className="text-purple-500">{currentView || 'overview'}</span>
          </div>
        </div>

        {/* Center - Status indicators */}
        <div className="flex items-center gap-4">
          {/* Terminal Status */}
          <div className="flex items-center gap-1">
            {isTerminalConnected ? (
              <Wifi className="h-3 w-3 text-green-500" />
            ) : (
              <WifiOff className="h-3 w-3 text-red-500" />
            )}
            <span className={isTerminalConnected ? 'text-green-500' : 'text-red-500'}>
              Terminal
            </span>
          </div>

          {/* Authentication Status */}
          <div className="flex items-center gap-1">
            <User className="h-3 w-3" />
            <span className={isAuthenticated ? 'text-green-500' : 'text-red-500'}>
              {isAuthenticated ? (user?.email || 'Authenticated') : 'Not authenticated'}
            </span>
          </div>

          {/* Todos Count */}
          {todos && todos.length > 0 && (
            <div className="flex items-center gap-1">
              <Activity className="h-3 w-3 text-orange-500" />
              <span className="text-orange-500">{todos.length} todos</span>
            </div>
          )}

          {/* Subagents Count */}
          {subagents && subagents.length > 0 && (
            <div className="flex items-center gap-1">
              <Bot className="h-3 w-3 text-purple-500" />
              <span className="text-purple-500">{subagents.length} agents</span>
            </div>
          )}
        </div>

        {/* Right side - Metrics */}
        <div className="flex items-center gap-4">
          {/* Cost */}
          <div className="flex items-center gap-1">
            <DollarSign className="h-3 w-3 text-green-500" />
            <span className="text-green-500">{formatCost(cost)}</span>
          </div>

          {/* Duration */}
          <div className="flex items-center gap-1">
            <Clock className="h-3 w-3 text-blue-500" />
            <span className="text-blue-500">{formatDuration(duration)}</span>
          </div>

          {/* Lines Changed */}
          {(linesAdded > 0 || linesRemoved > 0) && (
            <div className="flex items-center gap-1">
              <span className="text-green-500">+{linesAdded}</span>
              <span className="text-red-500">-{linesRemoved}</span>
            </div>
          )}

          {/* Settings */}
          <div className="flex items-center gap-1">
            <Settings className="h-3 w-3 text-muted-foreground" />
            <span className="text-muted-foreground">Byte DevOps IDE</span>
          </div>
        </div>
      </div>
    </div>
  );
}
